/**
 * Button Component
 */

'use client';

import styled from '@emotion/styled';
import { ComponentSize, ButtonType } from '../types';

export interface ButtonProps {
  text: string | React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  wide?: boolean;
  size?: ComponentSize;
  type?: ButtonType;
  className?: string;
  testId?: string;
}

const StyledButton = styled.button<{
  $wide?: boolean;
  $size: ComponentSize;
  $variant: 'primary' | 'secondary';
}>`
  width: ${({ $wide }) => ($wide ? '100%' : 'auto')};
  border: none;
  border-radius: ${({ theme }) => theme?.component?.button.borderRadius};
  font-weight: ${({ theme }) => theme?.component?.button.fontWeight};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  
  ${({ theme, $size }) => {
    const sizeMap = {
      [ComponentSize.XXSmall]: 'small',
      [ComponentSize.XSmall]: 'small',
      [ComponentSize.Small]: 'small',
      [ComponentSize.Medium]: 'medium',
      [ComponentSize.Large]: 'large',
    };
    const mappedSize = sizeMap[$size] as 'small' | 'medium' | 'large';
    const buttonSize = theme?.component?.button.size[mappedSize];
    return `
      font-size: ${buttonSize.fontSize};
      padding: ${buttonSize.padding};
    `;
  }}
  
  ${({ theme, $variant }) => {
    const variant = theme?.component?.button[$variant];
    return `
      background-color: ${variant.backgroundColor};
      color: ${variant.color};
      
      &:hover:not(:disabled) {
        background-color: ${variant.hoverBackgroundColor};
      }
    `;
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export function ButtonPrimary({
  text,
  onClick,
  disabled = false,
  wide = false,
  size = ComponentSize.Medium,
  type = 'button',
  className,
  testId = 'button-primary',
}: ButtonProps) {
  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled}
      type={type}
      data-testid={testId}
      $wide={wide}
      $size={size}
      $variant="primary"
      className={className}
    >
      {text}
    </StyledButton>
  );
}

export function ButtonSecondary({
  text,
  onClick,
  disabled = false,
  wide = false,
  size = ComponentSize.Medium,
  type = 'button',
  className,
  testId = 'button-secondary',
}: ButtonProps) {
  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled}
      type={type}
      data-testid={testId}
      $wide={wide}
      $size={size}
      $variant="secondary"
      className={className}
    >
      {text}
    </StyledButton>
  );
}

